import AUDIENCES_ERROR_MESSAGES from 'cio/utils/ad-audiences/error-messages';
import { GREEN_500, RED_500, GRAY_500, YELLOW_500 } from 'cio/utils/colors';
import { Provider } from 'cio/utils/oauth';

export type AdNetworkType = 'facebook' | 'adwords';
export type AdNetworkProvider = Provider.GoogleAds;
export type AdNetworkStatus = 'synced' | 'syncing' | 'pending' | 'paused' | 'error' | 'failed' | 'confirm_consent';

export const TYPES: Record<string, AdNetworkType> = {
  adwords: 'adwords',
  facebook: 'facebook',
};

export const NAMES = {
  adwords: 'Google Ads',
  facebook: 'Facebook Ads',
};

export const PROVIDER_MAP: { [key in AdNetworkType]: AdNetworkProvider | null } = {
  adwords: Provider.GoogleAds,
  facebook: null,
};

type SyncStatuses = {
  [key in AdNetworkStatus]: {
    id: string;
    title: string;
    label: string;
    type: string;
    color: string;
    explanation: (data: { name: string; time: string }) => string;
  };
};

export const SYNC_STATUSES: SyncStatuses = {
  synced: {
    id: 'synced',
    title: 'Synced',
    label: 'Synced',
    type: 'success',
    color: GREEN_500,
    explanation({ name, time }) {
      let suffix = time ? `at ${time}` : '';
      return `This segment successfully synced to ${name} ${suffix}`;
    },
  },
  syncing: {
    id: 'syncing',
    title: 'Actively syncing',
    label: 'Syncing',
    type: 'success',
    color: GREEN_500,
    explanation({ name }) {
      return `This segment is actively syncing to ${name}.`;
    },
  },
  pending: {
    id: 'pending',
    title: 'Queued to sync',
    label: 'Pending',
    type: 'warning',
    color: YELLOW_500,
    explanation({ name }) {
      return `This segment is queued to sync to ${name}.`;
    },
  },
  paused: {
    id: 'paused',
    title: 'Paused sync',
    label: 'Paused',
    type: 'pending',
    color: GRAY_500,
    explanation({ name }) {
      return `This segment sync is paused and will not automatically sync to ${name}.`;
    },
  },
  error: {
    id: 'error',
    title: 'Error syncing',
    label: 'Error',
    type: 'error',
    color: RED_500,
    explanation({ name }) {
      return `There was an error syncing this segment to ${name}.`;
    },
  },
  failed: {
    id: 'failed',
    title: 'Failed to sync',
    label: 'Failed',
    color: RED_500,
    type: 'error',
    explanation({ name }) {
      return `This segment failed to sync to ${name}.`;
    },
  },
  confirm_consent: {
    id: 'confirm_consent',
    title: 'Action required',
    label: 'Confirm consent',
    color: YELLOW_500,
    type: 'warning',
    explanation({ name }) {
      return `This segment requires consent to sync to ${name}.`;
    },
  },
};

export const formatAudiencesError = (error: string) => {
  let key = Object.keys(AUDIENCES_ERROR_MESSAGES).find((key) =>
    new RegExp(key).test(error),
  ) as keyof typeof AUDIENCES_ERROR_MESSAGES;

  return key ? AUDIENCES_ERROR_MESSAGES[key] : error;
};
